import { useState, useEffect } from "react";
import {
  ChevronLeft,
  Calendar,
  FileText,
} from "lucide-react";
import api from "../../../lib/api";
import { ZipAutoFillInput } from "../components/ZipAutoFillInput";
import { useLeadSources } from "../../../Hooks/useLeadSources";
import toast from "react-hot-toast";

export default function CreateQuote() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [loadingClients, setLoadingClients] = useState(false);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [form, setForm] = useState({
    service_id: "",
    boat_type: "",
    boat_make: "",
    boat_model: "",
    boat_year: "",
    boat_location: "",
    boat_length: "",
    hull_material: "",
    engine_model: "",
    engine_hp: "",
    engine_hours: "",
    fuel_type: "",
    location: "",
    zip_code: "",
    notes: "",
    lead_source_id: "",
  });
  const [quoteNumber, setQuoteNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const { leadSources = [], isLoading: loadingLeadSources } = useLeadSources();

  useEffect(() => {
    setLoadingClients(true);
    api
      .get("/company/client/all")
      .then((res) => {
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          setClients(res.data.data);
        }
      })
      .catch(() => setClients([]))
      .finally(() => setLoadingClients(false));
  }, []);

  useEffect(() => {
    setLoadingServices(true);
    api
      .get("/user/services")
      .then((res) => {
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          setServices(res.data.data);
        }
      })
      .catch(() => setServices([]))
      .finally(() => setLoadingServices(false));
  }, []);

  const today = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  // Auto-generates quote number in format: QUOTE-YYYYMMDD-XXXX
  // Uses localStorage to maintain a daily counter (resets each day)
  const generateQuoteNumber = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const dateKey = `${y}${m}${d}`;
    const storageKey = `quoteCounter-${dateKey}`;
    const current = Number(localStorage.getItem(storageKey) || "0") + 1;
    localStorage.setItem(storageKey, String(current));
    const counter = String(current).padStart(4, "0");
    return `QUOTE-${dateKey}-${counter}`;
  };

  // Auto-generate quote number on mount if empty
  useEffect(() => {
    if (!quoteNumber) {
      setQuoteNumber(generateQuoteNumber());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    // For service_id, always store as number if possible
    if (name === "service_id") {
      setForm((prev) => ({ ...prev, [name]: value ? Number(value) : "" }));
    } else if (name === "zip_code") {
      // Only allow numeric input and max 5 digits for zip_code
      const stringValue = String(value || "");
      if (stringValue === "" || (/^\d+$/.test(stringValue) && stringValue.length <= 5)) {
        setForm((prev) => ({ ...prev, [name]: value }));
      }
    } else if (name === "boat_length" || name === "boat_year") {
      // Only allow numeric input for these fields (integers only)
      const stringValue = String(value || "");
      if (stringValue === "" || /^\d+$/.test(stringValue)) {
        setForm((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccessMsg("");
    setErrorMsg("");
    if (!selectedClientId) {
      setErrorMsg("Please select a client.");
      setSubmitting(false);
      return;
    }
    // Ensure quote number is set, regenerate if empty
    let finalQuoteNumber = quoteNumber;
    if (!finalQuoteNumber.trim()) {
      finalQuoteNumber = generateQuoteNumber();
      setQuoteNumber(finalQuoteNumber);
    }
    try {
      const payload = {
        client_id: Number(selectedClientId),
        quote_number: finalQuoteNumber,
        service_id: form.service_id,
        boat_type: form.boat_type,
        boat_make: form.boat_make,
        boat_model: form.boat_model,
        boat_year: form.boat_year,
        boat_location: form.boat_location,
        boat_length: form.boat_length,
        hull_material: form.hull_material,
        engine_model: form.engine_model,
        engine_hp: form.engine_hp,
        engine_hours: form.engine_hours,
        fuel_type: form.fuel_type,
        location: form.location,
        zip_code: form.zip_code,
        notes: form.notes,
        lead_source_id: form.lead_source_id ? Number(form.lead_source_id) : null,
      };
      await api.post("/company/quotes/create-for-client", payload);
      toast.success("Quote created successfully!");
      setSuccessMsg("Quote created successfully!");
      setForm({
        service_id: "",
        boat_type: "",
        boat_make: "",
        boat_model: "",
        boat_year: "",
        boat_location: "",
        boat_length: "",
        hull_material: "",
        engine_model: "",
        engine_hp: "",
        engine_hours: "",
        fuel_type: "",
        location: "",
        zip_code: "",
        notes: "",
        lead_source_id: "",
      });
      setSelectedClientId("");
      // Generate new quote number for next quote
      setQuoteNumber(generateQuoteNumber());
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to create quote.";
      toast.error(msg);
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="min-h-screen bg-gradient-to-br from-blue-50 to-white pb-10">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button className="p-2 hover:bg-blue-100 rounded-lg transition">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-blue-900">
                    New Quote
                  </h1>
                  <p className="text-xs text-blue-500">
                    Create a professional quote for your client
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 rounded-lg border border-blue-200">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-medium text-blue-900">
                  {today}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form
        className="px-4 py-8 space-y-8 max-w-2xl mx-auto"
        onSubmit={handleSubmit}
      >
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Quote Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={quoteNumber}
                readOnly
                placeholder="Auto-generating..."
                className="w-full px-4 py-3 border border-blue-100 rounded-lg text-base bg-blue-50/50 text-blue-400 cursor-not-allowed"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Client <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="w-full px-4 py-3 border border-blue-200 rounded-lg text-base bg-blue-50 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                required
                disabled={loadingClients}
              >
                <option value="">
                  {loadingClients ? "Loading clients..." : "Select client"}
                </option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.first_name || ""} {client.last_name || ""} -{" "}
                    {client.email || client.phone || ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Service <span className="text-red-500">*</span>
              </label>
              <select
                name="service_id"
                value={form.service_id}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border border-blue-200 rounded-lg text-base bg-blue-50 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                required
                disabled={loadingServices}
              >
                <option value="">
                  {loadingServices ? "Loading services..." : "Select service"}
                </option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Boat Type
              </label>
              <select
                name="boat_type"
                value={form.boat_type}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border border-blue-200 rounded-lg text-base bg-blue-50 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              >
                <option value="">Select boat type</option>
                <option value="Power Boat">Power Boat</option>
                <option value="Sailboat">Sailboat</option>
                <option value="Yacht">Yacht</option>
                <option value="Catamaran">Catamaran</option>
                <option value="Fishing Boat">Fishing Boat</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Boat Manufacturer              </label>
              <input
                type="text"
                name="boat_make"
                value={form.boat_make}
                onChange={handleFormChange}
                placeholder="Enter boat make"
                className="w-full px-4 py-3 border border-blue-200 rounded-lg text-base bg-blue-50 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Boat Model
              </label>
              <input
                type="text"
                name="boat_model"
                value={form.boat_model}
                onChange={handleFormChange}
                placeholder="Enter boat model"
                className="w-full px-4 py-3 border border-blue-200 rounded-lg text-base bg-blue-50 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Boat Year
              </label>
              <input
                type="number"
                name="boat_year"
                value={form.boat_year}
                onChange={handleFormChange}
                placeholder="2023"
                className="w-full px-4 py-3 border border-blue-200 rounded-lg text-base bg-blue-50 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Boat Length
              </label>
              <input
                type="number"
                name="boat_length"
                value={form.boat_length}
                onChange={handleFormChange}
                placeholder="40"
                className="w-full px-4 py-3 border border-blue-200 rounded-lg text-base bg-blue-50 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Lead Source
              </label>
              <select
                name="lead_source_id"
                value={form.lead_source_id}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border border-blue-200 rounded-lg text-base bg-blue-50 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
                disabled={loadingLeadSources}
              >
                <option value="">Select lead source</option>
                {leadSources.map((source) => (
                  <option key={source.id} value={source.id}>
                    {source.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Zip Code
              </label>
              <ZipAutoFillInput
                zip={form.zip_code}
                onZipChange={(val) => setForm(prev => ({ ...prev, zip_code: val }))}
                onAddressResolved={({ city, state, country }) => {
                  const parts = [city, state, country].filter(Boolean);
                  if (parts.length > 0) {
                    setForm(prev => ({ ...prev, location: parts.join(', ') }));
                  }
                }}
                className="w-full px-4 py-3 border border-blue-200 rounded-lg text-base bg-blue-50 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Boat Location
              </label>
              <input
                type="text"
                name="boat_location"
                value={form.location}
                onChange={handleFormChange}
                placeholder="Enter Boat Location"
                className="w-full px-4 py-3 border border-blue-200 rounded-lg text-base bg-blue-50 focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-blue-900 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleFormChange}
                placeholder="Enter any additional notes"
                className="w-full px-4 py-3 border border-blue-200 rounded-lg text-base bg-blue-50 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-none"
              />
            </div>
          </div>
          {errorMsg && (
            <div className="text-red-600 text-xs mt-4 font-semibold">
              {errorMsg}
            </div>
          )}
          {successMsg && (
            <div className="text-green-600 text-xs mt-4 font-semibold">
              {successMsg}
            </div>
          )}
          <div className="flex justify-end mt-8">
            <button
              type="submit"
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-base font-bold shadow-md hover:shadow-lg transition disabled:opacity-60"
              disabled={submitting}
            >
              {submitting ? "Creating..." : "Create Quote"}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}
